import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useState } from "react";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import {
  OFFLINE_PAYMENT_MODES,
  PaymentModeEnum,
} from "../../enums/PaymentModeEnum";
import ComboService from "../../services/ComboService";
import { CustomSnackbarTypes } from "./CustomSnackBar";

interface MarkComboPaidDialogProps {
  open: boolean;
  comboOrderId?: number;
  amount?: string;
  customerPhone?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const comboService = new ComboService();

const MarkComboPaidDialog = ({
  open,
  comboOrderId,
  amount,
  customerPhone,
  onClose,
  onSuccess,
}: MarkComboPaidDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [paymentMode, setPaymentMode] = useState<PaymentModeEnum>(
    PaymentModeEnum.CASH,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPaymentMode(PaymentModeEnum.CASH);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!comboOrderId) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Combo order id is missing",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await comboService.markComboAsPaid(comboOrderId, paymentMode);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Pack marked as paid. Coupons have been generated.",
        type: CustomSnackbarTypes.SUCCESS,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message:
          error?.response?.data?.error ??
          error?.response?.data?.message ??
          "Failed to mark the pack as paid",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: "100%",
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>Mark Pack as Paid</DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Use this only when the money has already been collected outside
          razorpay. This generates the coupons, activates the linked ChefIt
          visits and cannot be undone.
        </Alert>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Amount to be recorded
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {`₹ ${amount ?? "-"}`}
          </Typography>
          {customerPhone && (
            <Typography variant="caption" color="text.secondary">
              {`Customer: ${customerPhone}`}
            </Typography>
          )}
        </Box>
        <FormControl fullWidth size="small">
          <InputLabel id="mark-paid-payment-mode-label">Payment mode</InputLabel>
          <Select
            labelId="mark-paid-payment-mode-label"
            id="mark-paid-payment-mode"
            label="Payment mode"
            value={paymentMode}
            onChange={(event) =>
              setPaymentMode(event.target.value as PaymentModeEnum)
            }
          >
            {OFFLINE_PAYMENT_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {mode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.75, ml: 0.25 }}
          color="text.secondary"
        >
          A receipt is created against the pack with this mode
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: "flex-start",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          {isSubmitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Mark as Paid"
          )}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarkComboPaidDialog;
